// src/context/CountryContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createAuditedClient } from '../utils/auditClient';
import { fetchAuthSession } from 'aws-amplify/auth';

const client = createAuditedClient('CountryContext');
const COUNTRY_STORAGE_KEY = 'haste.console.selectedCountry';

/**
 * IMPORTANT:
 * The `value` here MUST MATCH what you store in Company.country.
 * If your DB has "Georgia" / "Albania" as plain strings, use those.
 * If it has "GE" / "AL", use that instead.
 */
export const COUNTRY_OPTIONS = [
  { label: 'Georgia', value: 'Georgia' },
  { label: 'Albania', value: 'Albania' },
];

const ListCompaniesByCountry = /* GraphQL */ `
  query ListCompaniesByCountry($country: String) {
    listCompanies(filter: { country: { eq: $country } }, limit: 10) {
      items { id name country }
      nextToken
    }
  }
`;

const CountryContext = createContext(null);

function getInitialCountry() {
  if (typeof window === 'undefined') return COUNTRY_OPTIONS[0].value;
  try {
    const saved = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (!saved) return COUNTRY_OPTIONS[0].value;
    const valid = COUNTRY_OPTIONS.some((opt) => opt.value === saved);
    return valid ? saved : COUNTRY_OPTIONS[0].value;
  } catch {
    return COUNTRY_OPTIONS[0].value;
  }
}

export function CountryProvider({ children }) {
  // Which country is selected in the toggle
  const [country, setCountry] = useState(getInitialCountry);

  // Active company derived from either:
  // - admin + country toggle → lookup company by country
  // - non-admin → fixed from their token
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [loadingCompany, setLoadingCompany] = useState(false);
  const [companyError, setCompanyError] = useState(null);

  // Auth info: groups + company from token
  const [groups, setGroups] = useState([]);
  const [tokenCompanyId, setTokenCompanyId] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);

  // 1) Load auth info once
  useEffect(() => {
    (async () => {
      try {
        const { tokens } = await fetchAuthSession();
        const p = tokens?.idToken?.payload || {};

        setGroups(p['cognito:groups'] || []);
        setTokenCompanyId(
          p['custom:CompanyId'] ?? p['custom:companyId'] ?? null
        );
      } catch (err) {
        console.error('CountryContext: failed to load auth session', err);
        setGroups([]);
        setTokenCompanyId(null);
      } finally {
        setAuthLoaded(true);
      }
    })();
  }, []);

  // Persist selected country so refresh keeps the same agency context.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, String(country || ''));
    } catch {
      // no-op
    }
  }, [country]);

  // 2) Resolve active company whenever auth or country changes
  useEffect(() => {
    if (!authLoaded) return;

    const isAdmin = groups.includes('admin');

    // Non-admins are locked to the company from their token
    if (!isAdmin) {
      setSelectedCompanyId(tokenCompanyId || null);
      setSelectedCompany(null);
      setLoadingCompany(false);
      setCompanyError(null);
      return;
    }

    // Admins: use the country toggle to find the company
    if (!country) return;

    let cancelled = false;
    setLoadingCompany(true);
    setCompanyError(null);

    client
      .graphql({
        query: ListCompaniesByCountry,
        variables: { country },
        authMode: 'userPool',
      })
      .then((res) => {
        if (cancelled) return;
        const items = res?.data?.listCompanies?.items || [];
        if (!items.length) {
          setSelectedCompanyId(null);
          setSelectedCompany(null);
          setCompanyError(
            `No Company found for country "${country}". Check Company.country in the DB.`
          );
          return;
        }

        // You said: one agency per country → take the first.
        const company = items[0];
        setSelectedCompanyId(company.id);
        setSelectedCompany(company);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('CountryContext: failed to load company by country', err);
        setSelectedCompanyId(null);
        setSelectedCompany(null);
        setCompanyError('Failed to load company for selected country');
      })
      .finally(() => {
        if (!cancelled) setLoadingCompany(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoaded, groups, country, tokenCompanyId]);

  return (
    <CountryContext.Provider
      value={{
        // UI
        country,
        setCountry,

        // the important part
        selectedCompanyId,
        selectedCompany,
        loadingCompany,
        companyError,

        // useful extra info if you need it later
        groups,
        tokenCompanyId,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    throw new Error('useCountry must be used inside CountryProvider');
  }
  return ctx;
}
